<template>
  <AppLayout title="Account & Passwort">
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div class="flex justify-between items-center">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">{{ $t('Account & Passwort') }}</h1>
              <p class="text-sm text-gray-600 mt-1">{{ $t('Verwalten Sie Ihre persönlichen Daten und Sicherheitseinstellungen') }}</p>
            </div>
            <Link 
              :href="route('customerAccount')" 
              class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              {{ $t('Zurück zum Konto') }}
            </Link>
          </div>
        </div>
      </div>

      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Update Profile Data -->
          <div class="bg-white shadow rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200">
              <h2 class="text-lg font-semibold text-gray-900">{{ $t('Daten aktualisieren') }}</h2>
            </div>
            <form @submit.prevent="updateProfile" class="p-6">
              <div class="space-y-6">
                <div>
                  <label for="name" class="block text-sm font-medium text-gray-700">
                    {{ $t('Name') }}
                  </label>
                  <input
                    id="name"
                    v-model="profileForm.name"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500"
                    :class="{ 'border-red-500': profileForm.errors.name }"
                  >
                  <p v-if="profileForm.errors.name" class="mt-1 text-sm text-red-600">{{ profileForm.errors.name }}</p>
                </div>

                <div>
                  <label for="email" class="block text-sm font-medium text-gray-700">
                    {{ $t('E-Mail-Adresse') }}
                  </label>
                  <input
                    id="email"
                    v-model="profileForm.email"
                    type="email"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500"
                    :class="{ 'border-red-500': profileForm.errors.email }"
                  >
                  <p v-if="profileForm.errors.email" class="mt-1 text-sm text-red-600">{{ profileForm.errors.email }}</p>
                  <p class="mt-2 text-sm text-gray-600">
                    <em>{{ $t('Sie erhalten eine E-Mail zur Bestätigung Ihrer neuen E-Mail-Adresse. Bis dahin werden Sie abgemeldet.') }}</em>
                  </p>
                </div>
              </div>

              <div class="mt-6 flex justify-end">
                <button
                  type="submit"
                  :disabled="profileForm.processing"
                  class="bg-sonnenglas-600 text-white px-4 py-2 rounded-lg hover:bg-sonnenglas-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="profileForm.processing" class="flex items-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ $t('Wird gespeichert...') }}
                  </span>
                  <span v-else>{{ $t('Daten aktualisieren') }}</span>
                </button>
              </div>
            </form>
          </div>

          <!-- Change Password -->
          <div class="bg-white shadow rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200">
              <h2 class="text-lg font-semibold text-gray-900">{{ $t('Passwort ändern') }}</h2>
            </div>
            <form @submit.prevent="changePassword" class="p-6">
              <div class="space-y-6">
                <div>
                  <label for="current_password" class="block text-sm font-medium text-gray-700">
                    {{ $t('Aktuelles Passwort') }}
                  </label>
                  <input
                    id="current_password"
                    v-model="passwordForm.current_password"
                    type="password"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500"
                    :class="{ 'border-red-500': passwordForm.errors.current_password }"
                  >
                  <p v-if="passwordForm.errors.current_password" class="mt-1 text-sm text-red-600">{{ passwordForm.errors.current_password }}</p>
                </div>

                <div>
                  <label for="password" class="block text-sm font-medium text-gray-700">
                    {{ $t('Neues Passwort') }}
                  </label>
                  <input
                    id="password"
                    v-model="passwordForm.password"
                    type="password"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500"
                    :class="{ 'border-red-500': passwordForm.errors.password }"
                  >
                  <p v-if="passwordForm.errors.password" class="mt-1 text-sm text-red-600">{{ passwordForm.errors.password }}</p>
                </div>

                <div>
                  <label for="password_confirmation" class="block text-sm font-medium text-gray-700">
                    {{ $t('Neues Passwort bestätigen') }}
                  </label>
                  <input
                    id="password_confirmation"
                    v-model="passwordForm.password_confirmation"
                    type="password"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500"
                    :class="{ 'border-red-500': passwordForm.errors.password_confirmation }"
                  >
                  <p v-if="passwordForm.errors.password_confirmation" class="mt-1 text-sm text-red-600">{{ passwordForm.errors.password_confirmation }}</p>
                  <p class="mt-2 text-sm text-gray-600">
                    <em>{{ $t('Das Passwort muss mindestens 8 Zeichen lang sein.') }}</em>
                  </p>
                </div>
              </div>

              <div class="mt-6 flex justify-end">
                <button
                  type="submit"
                  :disabled="passwordForm.processing"
                  class="bg-sonnenglas-600 text-white px-4 py-2 rounded-lg hover:bg-sonnenglas-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="passwordForm.processing" class="flex items-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ $t('Wird gespeichert...') }}
                  </span>
                  <span v-else>{{ $t('Passwort ändern') }}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Newsletter Section -->
        <div class="mt-8 bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('Newsletter-Einstellungen') }}</h2>
          </div>
          <div class="p-6">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h3 class="text-sm font-medium text-gray-900">{{ $t('Newsletter abonnieren') }}</h3>
                <p class="text-sm text-gray-600 mt-1">{{ $t('Bleiben Sie über Neuigkeiten und Angebote informiert') }}</p>
              </div>
              <div class="ml-4">
                <button
                  @click="toggleNewsletter"
                  :disabled="newsletterProcessing"
                  :class="[
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sonnenglas-500 focus:ring-offset-2',
                    isSubscribedToNewsletter ? 'bg-sonnenglas-600' : 'bg-gray-200',
                    newsletterProcessing ? 'opacity-50 cursor-not-allowed' : ''
                  ]"
                >
                  <span
                    :class="[
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                      isSubscribedToNewsletter ? 'translate-x-5' : 'translate-x-0'
                    ]"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, inject } from 'vue'
import { Link, useForm, router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const route = inject<(name?: string, params?: any, absolute?: boolean) => string>('route')

interface User {
  id: number
  name: string
  email: string
}

interface Props {
  user: User
  isSubscribedToNewsletter: boolean
}

const props = defineProps<Props>()

const newsletterProcessing = ref(false)

// Profile form
const profileForm = useForm({
  name: props.user.name,
  email: props.user.email,
  action: 'change-data'
})

// Password form
const passwordForm = useForm({
  current_password: '',
  password: '',
  password_confirmation: '',
  action: 'change-password'
})

const updateProfile = () => {
  if (!route) {
    console.error('Route helper not available')
    return
  }

  profileForm.post(route('updateUserAccount'), {
    preserveScroll: true,
    onSuccess: () => {
      // Form will redirect or show success message
    }
  })
}

const changePassword = () => {
  if (!route) {
    console.error('Route helper not available')
    return
  }

  passwordForm.post(route('updateUserAccount'), {
    preserveScroll: true,
    onSuccess: () => {
      // Clear form on success
      passwordForm.reset('current_password', 'password', 'password_confirmation')
    }
  })
}

const toggleNewsletter = () => {
  if (!route) {
    console.error('Route helper not available')
    return
  }

  newsletterProcessing.value = true

  const routeName = props.isSubscribedToNewsletter
    ? 'newsletter.unsubscribe'
    : 'newsletter.subscribe'

  router.post(route(routeName), { email: props.user.email }, {
    preserveScroll: true,
    onFinish: () => {
      newsletterProcessing.value = false
    },
  })
}
</script>